// ReportInsights.model.ts

/* -------------------------------------------------------------------------- */
/*                               ANCHOR LATEST                                */
/* -------------------------------------------------------------------------- */

/**
 * Fila reducida de la lista de últimos reportes del dashboard.
 *
 * No reusa `Report`: ese modelo arrastra `houseFlat` completo (invitaciones,
 * residentes, pagos, dispositivos) y el endpoint de insights solo manda las
 * columnas que pinta la tarjeta.
 */
export interface IReportLatest {
  reportId: string;
  title: string;
  status: number;
  createdAt: string;
  streetBuildingName?: string | null;
  houseFlatNumber?: string | null;
  amenityName?: string | null;
}

export class ReportLatest {

  reportId: string;
  title: string;
  status: number;
  createdAt: string;
  streetBuildingName?: string;
  houseFlatNumber?: string;
  amenityName?: string;

  constructor(
    data: IReportLatest
  ) {
    this.reportId = data.reportId;
    this.title = data.title;
    this.status = Number(data.status);
    this.createdAt = data.createdAt;
    this.streetBuildingName = data.streetBuildingName ?? undefined;
    this.houseFlatNumber = data.houseFlatNumber ?? undefined;
    this.amenityName = data.amenityName ?? undefined;
  }

}

/* -------------------------------------------------------------------------- */
/*                            ANCHOR STATUS SUMMARY                           */
/* -------------------------------------------------------------------------- */

export interface IReportStatusSummary {
  status: number;
  name: string;
  count: number;
  percentage: string;
}

export class ReportStatusSummary {

  status: number;
  name: string;
  count: number;
  percentage: string;

  constructor(
    data: IReportStatusSummary
  ) {
    this.status = Number(data.status);
    this.name = data.name;
    this.count = Number(data.count ?? 0);
    this.percentage = data.percentage ?? '0.0';
  }

}

/* -------------------------------------------------------------------------- */
/*                           ANCHOR MONTHLY SUMMARY                           */
/* -------------------------------------------------------------------------- */

export interface IReportMonthlySummary {
  month: string;
  label: string;
  pendings: number;
  inProgress: number;
  resolved: number;
  canceled: number;
  total: number;
}

export class ReportMonthlySummary {

  month: string;
  label: string;
  pendings: number;
  inProgress: number;
  resolved: number;
  canceled: number;
  total: number;

  constructor(
    data: IReportMonthlySummary
  ) {
    this.month = data.month;
    this.label = data.label;
    this.pendings = Number(data.pendings ?? 0);
    this.inProgress = Number(data.inProgress ?? 0);
    this.resolved = Number(data.resolved ?? 0);
    this.canceled = Number(data.canceled ?? 0);
    this.total = Number(data.total ?? 0);
  }

}

/* -------------------------------------------------------------------------- */
/*                               ANCHOR INSIGHTS                              */
/* -------------------------------------------------------------------------- */

/**
 * Respuesta de GET /neighborhoods/{neighborhoodId}/reports/insights.
 *
 * `monthVariationDirection` maneja los mismos valores que el tipo
 * `MonthVariationDirection` de neighborhood-page.component.ts, para que la
 * tarjeta de variación mensual de reportes se pinte con la misma lógica que la
 * de visitas sin castear nada.
 */
export interface IReportInsights {
  total: number;
  pendings: number;
  inProgress: number;
  resolved: number;
  canceled: number;
  pendingsPercentage: string;
  inProgressPercentage: string;
  resolvedPercentage: string;
  canceledPercentage: string;
  /* Ojo: `resolutionRate` es resueltos sobre ACTIVOS, no sobre el total.
     `resolvedPercentage` sí es sobre el total; desde que existen los cancelados
     los dos valores dejaron de coincidir. */
  resolutionRate: string;
  averageResolutionHours?: number | null;
  monthTotal: number;
  previousMonthTotal: number;
  monthVariationPercentage?: string | null;
  monthVariationDirection: 'up' | 'down' | 'flat' | 'none';
  statusSummary: IReportStatusSummary[];
  monthlySummary: IReportMonthlySummary[];
  latest: IReportLatest[];
}

export class ReportInsights {

  total: number;
  pendings: number;
  inProgress: number;
  resolved: number;
  canceled: number;
  pendingsPercentage: string;
  inProgressPercentage: string;
  resolvedPercentage: string;
  canceledPercentage: string;
  resolutionRate: string;
  averageResolutionHours?: number;
  monthTotal: number;
  previousMonthTotal: number;
  monthVariationPercentage?: string;
  monthVariationDirection: 'up' | 'down' | 'flat' | 'none';
  statusSummary: ReportStatusSummary[];
  monthlySummary: ReportMonthlySummary[];
  latest: ReportLatest[];

  constructor(
    data: IReportInsights
  ) {
    this.total = Number(data.total ?? 0);
    this.pendings = Number(data.pendings ?? 0);
    this.inProgress = Number(data.inProgress ?? 0);
    this.resolved = Number(data.resolved ?? 0);
    this.canceled = Number(data.canceled ?? 0);
    this.pendingsPercentage = data.pendingsPercentage ?? '0.0';
    this.inProgressPercentage = data.inProgressPercentage ?? '0.0';
    this.resolvedPercentage = data.resolvedPercentage ?? '0.0';
    this.canceledPercentage = data.canceledPercentage ?? '0.0';
    this.resolutionRate = data.resolutionRate ?? '0.0';

    /* El backend manda null cuando no hay resueltos con los que promediar: se
       normaliza a undefined para que el componente use un solo chequeo. */
    this.averageResolutionHours = data.averageResolutionHours === null
      || data.averageResolutionHours === undefined
        ? undefined
        : Number(data.averageResolutionHours);

    this.monthTotal = Number(data.monthTotal ?? 0);
    this.previousMonthTotal = Number(data.previousMonthTotal ?? 0);

    this.monthVariationPercentage = data.monthVariationPercentage === null
      || data.monthVariationPercentage === undefined
        ? undefined
        : data.monthVariationPercentage;

    this.monthVariationDirection = data.monthVariationDirection ?? 'none';

    this.statusSummary = data.statusSummary
      ? data.statusSummary.map(
          item => new ReportStatusSummary(item)
        )
      : [];

    this.monthlySummary = data.monthlySummary
      ? data.monthlySummary.map(
          item => new ReportMonthlySummary(item)
        )
      : [];

    this.latest = data.latest
      ? data.latest.map(
          report => new ReportLatest(report)
        )
      : [];
  }

}
